/**
 * Elevation chart auto-initialization.
 * Reads elevation profile data from container attributes and creates ElevationChart instances.
 * Registers a MutationObserver to auto-init charts when SSE content is inserted
 * (inline script tags don't execute when inserted via innerHTML).
 */

import { ElevationChart } from './charts';
import { COLORS } from './colors';
import { createHoverMarkerElement } from './maps';
import type { Marker } from 'mapbox-gl';
import type { LngLat } from './types';

// Module-level observer reference for cleanup
let chartObserver: MutationObserver | null = null;

/**
 * Initialize an elevation chart for a given canvas ID.
 * Reads data-elevation-profile and data-trail-coords from the container element.
 */
export function initElevationChart(canvasId: string): void {
    try {
        const container = document.getElementById(canvasId + '-container');
        if (!container) return;

        const profileJson = container.getAttribute('data-elevation-profile');
        const coordsJson = container.getAttribute('data-trail-coords');
        if (!profileJson) return;

        const data = JSON.parse(profileJson);
        if (!data || !data.distances || !data.elevations) return;
        if (typeof Chart === 'undefined') return;

        // Sample trail coordinates to match profile points
        let coordinates: LngLat[] = [];
        if (coordsJson) {
            try {
                const rawCoords = JSON.parse(coordsJson);
                // Flatten if MultiLineString (array of arrays)
                const flatCoords: LngLat[] = Array.isArray(rawCoords[0][0])
                    ? rawCoords.flat()
                    : rawCoords;

                // Sample to match profile length
                const numPoints = data.distances.length;
                if (flatCoords.length > 0) {
                    const step = (flatCoords.length - 1) / (numPoints - 1);
                    for (let i = 0; i < numPoints; i++) {
                        const idx = Math.min(Math.round(i * step), flatCoords.length - 1);
                        coordinates.push(flatCoords[idx]);
                    }
                }
            } catch (e) {
                console.warn('Failed to parse trail coords:', e);
            }
        }

        // Create hover marker (will be shown/hidden on chart hover)
        let hoverMarker: Marker | null = null;
        if (window.discoverMap && coordinates.length > 0 && typeof mapboxgl !== 'undefined') {
            const markerEl = createHoverMarkerElement(COLORS.primary, 16);
            hoverMarker = new mapboxgl.Marker({ element: markerEl })
                .setLngLat(coordinates[0])
                .addTo(window.discoverMap);
            markerEl.style.display = 'none';
        }

        new ElevationChart(canvasId, {
            distances: data.distances,
            elevations: data.elevations,
            coordinates: coordinates
        }, {
            onHover: function(info) {
                if (!hoverMarker) return;
                const el = hoverMarker.getElement();
                if (info && info.lngLat) {
                    hoverMarker.setLngLat(info.lngLat);
                    el.style.display = 'block';
                } else {
                    el.style.display = 'none';
                }
            }
        });
    } catch (e) {
        console.error('initElevationChart error:', e);
    }
}

/**
 * Start a MutationObserver that auto-initializes elevation charts
 * when elements with data-elevation-profile are added to the DOM.
 */
export function startElevationChartObserver(): void {
    // Guard against double-start
    if (chartObserver) return;

    chartObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            mutation.addedNodes.forEach(function(node) {
                if (node.nodeType === 1) { // Element node
                    const el = node as Element;
                    // Check if the node itself has the attribute
                    if (el.hasAttribute && el.hasAttribute('data-elevation-profile')) {
                        const canvasId = el.id.replace('-container', '');
                        setTimeout(function() { initElevationChart(canvasId); }, 10);
                    }
                    // Check descendants
                    if (el.querySelectorAll) {
                        el.querySelectorAll('[data-elevation-profile]').forEach(function(child) {
                            const canvasId = child.id.replace('-container', '');
                            setTimeout(function() { initElevationChart(canvasId); }, 10);
                        });
                    }
                }
            });
        });
    });
    chartObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * Stop the elevation chart MutationObserver.
 * Call on SPA page transitions to prevent leaked observers.
 */
export function stopElevationChartObserver(): void {
    if (chartObserver) {
        chartObserver.disconnect();
        chartObserver = null;
    }
}
